/**
 * The authentication screen — everything a signed-out owner sees.
 *
 * One component, a small state machine: sign in (password → a required second
 * factor), first-run setup (email + password → enroll Google Authenticator →
 * save recovery codes), and password reset. It renders OUTSIDE the app shell,
 * so no nav, no websocket, no protected call is ever made before there is a
 * session. Styling uses the app's own tokens and primitives, so it themes with
 * the rest of the terminal.
 */

import { QRCodeSVG } from 'qrcode.react';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  KeyRound,
  Lock,
  Mail,
  Smartphone,
  Check,
  Copy,
  ArrowLeft,
} from 'lucide-react';

import { api, toApiError } from '@/api/client';
import { useSession } from '@/auth/AuthProvider';
import type {
  EnrollmentComplete,
  Established,
  MfaChallenge,
  RegisterStart,
} from '@/auth/types';
import { BrandLockup } from '@/components/layout/BrandMark';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { ToastViewport, toast } from '@/components/ui';

/** Remember the last email so the owner never re-types it (per-device only). */
const REMEMBER_KEY = 'bheema.lastEmail';
function getRememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
}
function rememberEmail(email: string): void {
  try {
    if (email) localStorage.setItem(REMEMBER_KEY, email.trim());
  } catch {
    /* private mode / blocked storage — a convenience, never load-bearing */
  }
}

type Mode =
  | 'login'
  | 'twofactor'
  | 'register'
  | 'enroll'
  | 'recovery'
  | 'reset-request'
  | 'reset-confirm';

const PASSWORD_RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'A number', test: (p) => /\d/.test(p) },
  { label: 'A symbol', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function LoginScreen() {
  const { status, refresh } = useSession();
  const firstRun = status?.first_run || status?.needs_setup;
  const emailConfigured = status?.email_configured ?? false;

  const [mode, setMode] = useState<Mode>(firstRun ? 'register' : 'login');
  // Prefill the last-used email so signing in is just password + code.
  const [email, setEmail] = useState<string>(() => getRememberedEmail());
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carried between steps.
  const [enroll, setEnroll] = useState<RegisterStart | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [methods, setMethods] = useState<string[]>([]);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [factor, setFactor] = useState<'totp' | 'email_otp' | 'recovery'>('totp');
  const [otpSent, setOtpSent] = useState(false);

  const policyOk = useMemo(() => PASSWORD_RULES.every((r) => r.test(password)), [password]);

  function reset(next: Mode) {
    setError(null);
    setCode('');
    setMode(next);
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  // ── Handlers ────────────────────────────────────────────────────────
  const onLogin = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<MfaChallenge>('/auth/login/password', { email, password });
      rememberEmail(email);
      setMethods(res.methods);
      setMaskedEmail(res.email_masked);
      setFactor(res.methods.includes('totp') ? 'totp' : 'email_otp');
      setPassword('');
      setOtpSent(false);
      reset('twofactor');
    });
  };

  const onRegister = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<RegisterStart>('/auth/register', { email, password });
      rememberEmail(email);
      setEnroll(res);
      setPassword('');
      reset('enroll');
    });
  };

  const onConfirmEnroll = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<EnrollmentComplete>('/auth/register/confirm-totp', {
        email: enroll?.email ?? email,
        code,
      });
      setRecoveryCodes(res.recovery_codes);
      reset('recovery');
    });
  };

  const finishAndEnter = () => {
    // Enrollment already established a session; drop into the app.
    refresh();
  };

  const onVerifyFactor = (e: FormEvent) => {
    e.preventDefault();
    const path =
      factor === 'totp'
        ? '/auth/login/totp'
        : factor === 'email_otp'
          ? '/auth/login/otp/verify'
          : '/auth/login/recovery';
    void run(async () => {
      await api.post<Established>(path, { code });
      refresh();
    });
  };

  const onRequestOtp = () => {
    void run(async () => {
      const res = await api.post<{ sent_to: string }>('/auth/login/otp/request');
      setMaskedEmail(res.sent_to);
      setOtpSent(true);
      toast.success('Code sent', `We emailed a code to ${res.sent_to}.`);
    });
  };

  const onResetRequest = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<{ message: string }>('/auth/password/reset/request', { email });
      toast.info('Check your email', res.message);
      reset('reset-confirm');
    });
  };

  const onResetConfirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.post('/auth/password/reset/confirm', {
        email,
        code,
        new_password: newPassword,
      });
      toast.success('Password reset', 'Sign in with your new password.');
      setNewPassword('');
      reset('login');
    });
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div
      className="flex min-h-full flex-1 items-center justify-center px-4 py-10 text-hi"
      style={{
        backgroundColor: 'var(--surface-0)',
        backgroundImage:
          'radial-gradient(900px 500px at 50% -8%, var(--accent-weak), transparent 62%)',
      }}
    >
      <div className="w-full max-w-[400px]">
        <LoginBrand />
        <div className="rounded-lg border border-border bg-surface-1 p-6 shadow-[var(--shadow-panel)]">
          {mode === 'login' && (
            <LoginForm
              email={email}
              password={password}
              busy={busy}
              error={error}
              onEmail={setEmail}
              onPassword={setPassword}
              onSubmit={onLogin}
              onForgot={emailConfigured ? () => reset('reset-request') : undefined}
            />
          )}

          {mode === 'twofactor' && (
            <TwoFactor
              factor={factor}
              methods={methods}
              maskedEmail={maskedEmail}
              code={code}
              busy={busy}
              error={error}
              otpSent={otpSent}
              onFactor={(f) => {
                setFactor(f);
                setCode('');
                setError(null);
                setOtpSent(false);
              }}
              onCode={setCode}
              onSubmit={onVerifyFactor}
              onRequestOtp={onRequestOtp}
              onBack={() => reset('login')}
            />
          )}

          {mode === 'register' && (
            <RegisterForm
              email={email}
              password={password}
              busy={busy}
              error={error}
              policyOk={policyOk}
              onEmail={setEmail}
              onPassword={setPassword}
              onSubmit={onRegister}
              onHaveAccount={() => reset('login')}
            />
          )}

          {mode === 'enroll' && enroll && (
            <Enroll
              enroll={enroll}
              code={code}
              busy={busy}
              error={error}
              onCode={setCode}
              onSubmit={onConfirmEnroll}
            />
          )}

          {mode === 'recovery' && (
            <RecoveryCodes codes={recoveryCodes} onDone={finishAndEnter} />
          )}

          {mode === 'reset-request' && (
            <ResetRequest
              email={email}
              busy={busy}
              error={error}
              onEmail={setEmail}
              onSubmit={onResetRequest}
              onBack={() => reset('login')}
            />
          )}

          {mode === 'reset-confirm' && (
            <ResetConfirm
              email={email}
              code={code}
              newPassword={newPassword}
              busy={busy}
              error={error}
              policyOk={PASSWORD_RULES.every((r) => r.test(newPassword))}
              onEmail={setEmail}
              onCode={setCode}
              onNewPassword={setNewPassword}
              onSubmit={onResetConfirm}
              onBack={() => reset('login')}
            />
          )}
        </div>
      </div>
      <ToastViewport />
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────
function LoginBrand() {
  return (
    <div className="mb-7 flex flex-col items-center gap-2.5 text-center">
      <BrandLockup />
    </div>
  );
}

function StepTitle({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="mt-0.5 text-accent" aria-hidden>
        {icon}
      </div>
      <div>
        <h1 className="text-[15px] font-semibold">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[12px] text-mid">{subtitle}</p>}
      </div>
    </div>
  );
}

function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      className="mb-4 rounded border border-danger/40 bg-danger-weak px-3 py-2 text-[12px] text-danger"
    >
      {error}
    </div>
  );
}

function LoginForm(props: {
  email: string;
  password: string;
  busy: boolean;
  error: string | null;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onForgot?: () => void;
}) {
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle icon={<Lock size={18} />} title="Sign in" subtitle="Your password, then a second factor." />
      <ErrorBanner error={props.error} />
      <div className="flex flex-col gap-3">
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          value={props.email}
          onChange={(e) => props.onEmail(e.target.value)}
          placeholder="you@example.com"
          prefix={<Mail size={13} />}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={props.password}
          onChange={(e) => props.onPassword(e.target.value)}
          required
        />
        <Button type="submit" variant="primary" size="lg" loading={props.busy} className="mt-1 w-full">
          Continue
        </Button>
      </div>
      {props.onForgot && (
        <button
          type="button"
          onClick={props.onForgot}
          className="mt-4 block w-full text-center text-[12px] text-accent hover:underline"
        >
          Forgot your password?
        </button>
      )}
    </form>
  );
}

function TwoFactor(props: {
  factor: 'totp' | 'email_otp' | 'recovery';
  methods: string[];
  maskedEmail: string;
  code: string;
  busy: boolean;
  error: string | null;
  otpSent: boolean;
  onFactor: (f: 'totp' | 'email_otp' | 'recovery') => void;
  onCode: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onRequestOtp: () => void;
  onBack: () => void;
}) {
  const { factor } = props;
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle
        icon={<Smartphone size={18} />}
        title="Two-factor verification"
        subtitle="Confirm it is you with a second factor."
      />
      <ErrorBanner error={props.error} />

      <div className="mb-4 flex gap-1 rounded-md bg-surface-inset p-1 text-[12px]">
        {props.methods.includes('totp') && (
          <FactorTab active={factor === 'totp'} onClick={() => props.onFactor('totp')}>
            Authenticator
          </FactorTab>
        )}
        {props.methods.includes('email_otp') && (
          <FactorTab active={factor === 'email_otp'} onClick={() => props.onFactor('email_otp')}>
            Email code
          </FactorTab>
        )}
        <FactorTab active={factor === 'recovery'} onClick={() => props.onFactor('recovery')}>
          Recovery
        </FactorTab>
      </div>

      {factor === 'totp' && (
        <Input
          label="6-digit code from your authenticator"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          value={props.code}
          onChange={(e) => props.onCode(e.target.value.replace(/\s/g, ''))}
          placeholder="123456"
          className="num tracking-[0.3em]"
        />
      )}

      {factor === 'email_otp' && (
        <div className="flex flex-col gap-3">
          {!props.otpSent ? (
            <div className="rounded border border-border bg-surface-inset px-3 py-3 text-[12px] text-mid">
              We&apos;ll email a one-time code to <span className="text-hi">{props.maskedEmail}</span>.
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={props.busy}
                onClick={props.onRequestOtp}
                className="mt-2 w-full"
              >
                Email me a code
              </Button>
            </div>
          ) : (
            <Input
              label={`Code sent to ${props.maskedEmail}`}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={props.code}
              onChange={(e) => props.onCode(e.target.value.replace(/\s/g, ''))}
              placeholder="123456"
              className="num tracking-[0.3em]"
            />
          )}
        </div>
      )}

      {factor === 'recovery' && (
        <Input
          label="One of your recovery codes"
          autoFocus
          value={props.code}
          onChange={(e) => props.onCode(e.target.value.toUpperCase())}
          placeholder="XXXXX-XXXXX"
          className="num tracking-widest"
        />
      )}

      {(factor !== 'email_otp' || props.otpSent) && (
        <Button type="submit" variant="primary" size="lg" loading={props.busy} className="mt-4 w-full">
          Verify &amp; sign in
        </Button>
      )}

      <button
        type="button"
        onClick={props.onBack}
        className="mt-4 flex w-full items-center justify-center gap-1 text-[12px] text-lo hover:text-hi"
      >
        <ArrowLeft size={12} /> Start over
      </button>
    </form>
  );
}

function FactorTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex-1 rounded px-2 py-1.5 font-medium transition-colors ' +
        (active ? 'bg-surface-3 text-hi shadow-sm' : 'text-lo hover:text-hi')
      }
    >
      {children}
    </button>
  );
}

function PasswordChecklist({ password }: { password: string }) {
  return (
    <ul className="mt-1 grid grid-cols-1 gap-1 text-[11px]">
      {PASSWORD_RULES.map((rule) => {
        const ok = rule.test(password);
        return (
          <li key={rule.label} className={ok ? 'flex items-center gap-1.5 text-up-text' : 'flex items-center gap-1.5 text-lo'}>
            <Check size={12} className={ok ? 'opacity-100' : 'opacity-30'} aria-hidden />
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
}

function RegisterForm(props: {
  email: string;
  password: string;
  busy: boolean;
  error: string | null;
  policyOk: boolean;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onHaveAccount: () => void;
}) {
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle
        icon={<KeyRound size={18} />}
        title="Create your account"
        subtitle="This device sets up the single owner account."
      />
      <ErrorBanner error={props.error} />
      <div className="flex flex-col gap-3">
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          value={props.email}
          onChange={(e) => props.onEmail(e.target.value)}
          placeholder="you@example.com"
          prefix={<Mail size={13} />}
          required
        />
        <div>
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            value={props.password}
            onChange={(e) => props.onPassword(e.target.value)}
            required
          />
          <PasswordChecklist password={props.password} />
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={props.busy}
          disabled={!props.policyOk}
          className="mt-1 w-full"
        >
          Continue to authenticator setup
        </Button>
      </div>
      <button
        type="button"
        onClick={props.onHaveAccount}
        className="mt-4 block w-full text-center text-[12px] text-lo hover:text-hi"
      >
        Already set up? Sign in
      </button>
    </form>
  );
}

function Enroll(props: {
  enroll: RegisterStart;
  code: string;
  busy: boolean;
  error: string | null;
  onCode: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle
        icon={<Smartphone size={18} />}
        title="Set up Google Authenticator"
        subtitle="Scan the QR with your authenticator app, then enter the code it shows."
      />
      <ErrorBanner error={props.error} />
      <div className="mb-4 flex flex-col items-center gap-3">
        <div className="rounded-lg bg-white p-3">
          <QRCodeSVG value={props.enroll.provisioning_uri} size={168} level="M" />
        </div>
        <details className="w-full text-[11px] text-lo">
          <summary className="cursor-pointer text-center hover:text-hi">Can&apos;t scan? Enter the key manually</summary>
          <code className="num mt-2 block break-all rounded bg-surface-inset px-2 py-1.5 text-center text-hi">
            {props.enroll.totp_secret}
          </code>
        </details>
      </div>
      <Input
        label="6-digit code from the app"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        value={props.code}
        onChange={(e) => props.onCode(e.target.value.replace(/\s/g, ''))}
        placeholder="123456"
        className="num tracking-[0.3em]"
      />
      <Button type="submit" variant="primary" size="lg" loading={props.busy} className="mt-4 w-full">
        Confirm &amp; continue
      </Button>
    </form>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(codes.join('\n')).then(
      () => {
        setCopied(true);
        toast.success('Copied', 'Recovery codes copied to the clipboard.');
      },
      () => toast.warn('Copy failed', 'Select and copy the codes manually.'),
    );
  };
  return (
    <div>
      <StepTitle
        icon={<KeyRound size={18} />}
        title="Save your recovery codes"
        subtitle="Each works once if you lose your authenticator. Store them somewhere safe — they are shown only now."
      />
      <div className="mb-4 grid grid-cols-2 gap-1.5 rounded border border-border bg-surface-inset p-3">
        {codes.map((c) => (
          <code key={c} className="num text-center text-[13px] text-hi">
            {c}
          </code>
        ))}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="secondary" size="md" icon={copied ? Check : Copy} onClick={copy} className="flex-1">
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button type="button" variant="primary" size="md" onClick={onDone} className="flex-1">
          I&apos;ve saved them — enter
        </Button>
      </div>
    </div>
  );
}

function ResetRequest(props: {
  email: string;
  busy: boolean;
  error: string | null;
  onEmail: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}) {
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle icon={<Mail size={18} />} title="Reset your password" subtitle="We'll email a one-time reset code." />
      <ErrorBanner error={props.error} />
      <Input
        label="Email"
        type="email"
        autoComplete="username"
        autoFocus
        value={props.email}
        onChange={(e) => props.onEmail(e.target.value)}
        placeholder="you@example.com"
        prefix={<Mail size={13} />}
        required
      />
      <Button type="submit" variant="primary" size="lg" loading={props.busy} className="mt-4 w-full">
        Email me a reset code
      </Button>
      <button type="button" onClick={props.onBack} className="mt-4 flex w-full items-center justify-center gap-1 text-[12px] text-lo hover:text-hi">
        <ArrowLeft size={12} /> Back to sign in
      </button>
    </form>
  );
}

function ResetConfirm(props: {
  email: string;
  code: string;
  newPassword: string;
  busy: boolean;
  error: string | null;
  policyOk: boolean;
  onEmail: (v: string) => void;
  onCode: (v: string) => void;
  onNewPassword: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}) {
  return (
    <form onSubmit={props.onSubmit} noValidate>
      <StepTitle icon={<Lock size={18} />} title="Enter your reset code" subtitle="Then choose a new password." />
      <ErrorBanner error={props.error} />
      <div className="flex flex-col gap-3">
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          value={props.email}
          onChange={(e) => props.onEmail(e.target.value)}
          required
        />
        <Input
          label="Reset code"
          inputMode="numeric"
          autoFocus
          value={props.code}
          onChange={(e) => props.onCode(e.target.value.replace(/\s/g, ''))}
          placeholder="12345678"
          className="num tracking-widest"
        />
        <div>
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            value={props.newPassword}
            onChange={(e) => props.onNewPassword(e.target.value)}
            required
          />
          <PasswordChecklist password={props.newPassword} />
        </div>
        <Button type="submit" variant="primary" size="lg" loading={props.busy} disabled={!props.policyOk} className="mt-1 w-full">
          Set new password
        </Button>
      </div>
      <button type="button" onClick={props.onBack} className="mt-4 flex w-full items-center justify-center gap-1 text-[12px] text-lo hover:text-hi">
        <ArrowLeft size={12} /> Back to sign in
      </button>
    </form>
  );
}
